import * as React from 'react'
import { Theme, useTheme } from '@mui/material/styles'
import { FC } from 'react'

import { ITemplateDetail } from '@user/store/agreements/typings'
import { TemplatePreview } from './Preview/Preview'

import Box from '@mui/material/Box'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import { InfoTooltip } from '@sales-center/components'

interface Props {
  details: ITemplateDetail | null
  isLoading: boolean
  templateId: number
  hasError: boolean
}

const getStyles = (theme: Theme) => ({
  icon: {
    position: 'absolute',
    borderRadius: '50%',
    backgroundColor: theme.palette.common.white,
    bottom: '-8px',
    right: '-8px',
    height: '16px',
    width: '16px',
  },

  checkIcon: {
    color: '#005137',
  },

  errorIcon: {
    color: theme.palette.error.main,
  },

  previewShadow: {
    boxShadow: '0 0 4px 0 rgba(20, 20, 20, 0.5)',
    position: 'relative',
    maxHeight: '200px',
  },

  templateName: {
    fontSize: '16px',
    fontFamily: 'Source Sans Pro',
  },
})

export const TemplateDetails: FC<Props> = (props) => {
  const { isLoading, details, hasError, templateId } = props
  const theme = useTheme()
  const styles = getStyles(theme)
  return (
    <Box sx={{ marginTop: theme.spacing(4.8) }}>
      <Box>
        <Typography variant="h7" sx={{ fontWeight: '600' }}>
          Template Details
        </Typography>
      </Box>
      <Box display="flex" sx={{ marginTop: theme.spacing(1.6) }}>
        {isLoading || !details ? (
          <Skeleton height={200} variant="rectangular" />
        ) : (
          <>
            <Box sx={styles.previewShadow}>
              <TemplatePreview templateId={templateId} />

              {hasError ? (
                <CancelIcon
                  sx={{ ...styles.icon, ...styles.errorIcon }}
                  data-test-id="agreementTemplatePreviewErrorIcon"
                />
              ) : (
                <CheckCircleIcon
                  sx={{ ...styles.icon, ...styles.checkIcon }}
                  data-test-id="agreementTemplatePreviewOkIcon"
                />
              )}
            </Box>

            <Box flexGrow={1} pl={1} sx={{ marginTop: theme.spacing(-0.3) }}>
              <Box>
                <Typography variant="subtitle2">
                  {details.templateName}
                </Typography>
              </Box>

              {details.isCardCaptureEnabled && (
                <Typography
                  variant="body2"
                  sx={{ marginTop: theme.spacing(0.4) }}
                >
                  This agreement will prompt the customer to <br /> put their
                  card on file.
                  <InfoTooltip
                    style={{
                      fontSize: '16px',
                      marginTop: theme.spacing(-0.3),
                    }}
                    infoText={
                      'Captured cards from forms will be stored on the Bill-to and visible in Sales Center under the Payments closing requirement.'
                    }
                    tooltipPlacement="right"
                  />
                </Typography>
              )}

              <Typography
                variant="body2"
                sx={{ marginBottom: theme.spacing(1) }}
              >
                This record will automatically be closed/won <br />
                and submitted to the Closed Won Queue after <br />
                the customer signs and completes the <br />
                agreement.
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}

TemplateDetails.displayName = 'TemplateDetails'
